import { Injectable } from '@angular/core';
import { STATIC_DATA } from '../shared/constants/static-data.constants';
import { NotificationService } from '../shared/services/notification.service';
import { formatUrl } from '../shared/utils/format-url-util';
import { BehaviorSubject, Observable } from 'rxjs';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class SectionsService {
  flow = '';
  private selected_enrollment?: any;
  private lead_submission_flow?: string;
  private tabChangedEvent$ = new BehaviorSubject<string>('');

  constructor(
    private notificationService: NotificationService,
    private userService: UserService
  ) { }

  changeTab(to: string) {
    this.tabChangedEvent$.next(to);
  }

  tabStatus(): Observable<any> {
    return this.tabChangedEvent$.asObservable();
  }

  clearTab() {
    this.tabChangedEvent$ = new BehaviorSubject<string>('');
  }

  getStep1(code: any) {
    const flow = this.getFlow();
    return require(`../../assets/data/programs/${code}/lead_logic/leadLogic_${flow}.json`);
  }

  getAllSteps(code: any) {
    const flow = this.getFlow();
    return require(`../../assets/data/programs/${code}/forms/${flow}/steps.json`);
  }

  getAdditionalDemoData(code: any) {
    return require(`../../assets/data/programs/${code}/forms/providerEnrollment/wotc/provider.json`);
  }

  getProviderSteps(code: any) {
    const enrollType = this.resolveEnrollType();
    return require(`../../assets/data/programs/${code}/forms/providerEnrollment/steps/${enrollType}.json`);
  }

  getEnrollmentAdditionData(code: any) {
    const flow = this.getFlow();
    return require(`../../assets/data/programs/${code}/enrollment/${flow}.json`);
  }

  getCDPAPData(code: any) {
    return require(`../../assets/data/programs/${code}/forms/cdpap/form.json`);
  }

  /** `flow` defaults to the shared flow; pass it when the caller resolves its own (e.g. embedded auth details). */
  getSectionData(code: any, source?: any, providerType?: any, flow = this.getFlow()) {
    // Condition for loading different provider types json files
    if (flow == 'providers' && providerType) {
      return require(`../../assets/data/programs/${code}/forms/${flow}/form_${providerType}.json`);
    }
    if (source == 'enrollment') {
      return require(`../../assets/data/programs/${code}/forms/participantEnrollment/associations/${flow}.json`);
    }
    if (source == 'payroll') {
      return require(`../../assets/data/programs/${code}/forms/payroll/${flow}/form.json`);
    }
    if (source === 'case-manager') {
      return require(`../../assets/data/programs/${code}/forms/caseManager/form.json`);
    }
    return require(`../../assets/data/programs/${code}/forms/${flow}/form.json`);
  }

  getProviderSectionData(code: any, section: string) {
    const enrollmentTypes = [...new Set<string>(this.getEnrollmentType())];

    if (enrollmentTypes.length <= 1) {
      const enrollType = enrollmentTypes[0] || this.resolveEnrollType();
      const config = require(`../../assets/data/programs/${code}/forms/providerEnrollment/associations/${enrollType}.json`);
      return config[section];
    }

    // Load config for each unique enrollment type
    const sectionConfigs: any[] = [];
    for (const enrollType of enrollmentTypes) {
      const config = require(`../../assets/data/programs/${code}/forms/providerEnrollment/associations/${enrollType}.json`);
      if (config[section]) {
        sectionConfigs.push(config[section]);
      }
    }

    if (sectionConfigs.length === 0) {
      return null;
    }

    if (sectionConfigs.length === 1) {
      return sectionConfigs[0];
    }

    return this.mergeSectionConfigs(sectionConfigs);
  }

  /**
   * Merges section configs from multiple provider types.
   * - Uses the primary (first) type as the base
   * - Adds unique attributes from other types
   * - For shared attributes: isAvailable = "Y" if ANY type has "Y", mandatory = "Y" if ANY type has "Y"
   * - Uses the primary type's label when labels conflict
   * - Unions validations by whatToCheck
   */
  private mergeSectionConfigs(sectionConfigs: any[]) {
    const base = JSON.parse(JSON.stringify(sectionConfigs[0]));
    const attributeMap = new Map<string, any>();

    // Index base attributes
    for (const attr of base.attributes) {
      attributeMap.set(attr.attribute_ID, attr);
    }

    // Merge attributes from remaining types
    for (let i = 1; i < sectionConfigs.length; i++) {
      const section = sectionConfigs[i];
      for (const attr of section.attributes ?? []) {
        const existing = attributeMap.get(attr.attribute_ID);
        if (existing) {
          // Field exists in base — widen availability and mandatory
          if (attr.isAvailable === 'Y') {
            existing.isAvailable = 'Y';
          }
          if (attr.mandatory === 'Y') {
            existing.mandatory = 'Y';
          }
        } else {
          // New field not in base — add it
          attributeMap.set(attr.attribute_ID, { ...attr });
        }
      }
    }

    base.attributes = Array.from(attributeMap.values());

    // Merge validations — union by whatToCheck
    const validationKeys = new Set(base.validations?.map((v: any) => v.whatToCheck) ?? []);
    for (let i = 1; i < sectionConfigs.length; i++) {
      for (const validation of sectionConfigs[i].validations ?? []) {
        if (!validationKeys.has(validation.whatToCheck)) {
          validationKeys.add(validation.whatToCheck);
          base.validations.push(validation);
        }
      }
    }

    return base;
  }

  setFlow(flow: any) {
    this.lead_submission_flow = flow;
  }

  getFlow() {
    return this.lead_submission_flow;
  }

  // // Set selected lead (store in-memory)
  // setSelectedLead(lead_submission_flow: string): void {
  //   this.lead_submission_flow = lead_submission_flow;
  // }

  // // Get selected lead (in-memory)
  // getSelectedLead(): string | undefined {
  //   return this.lead_submission_flow;
  // }

  // we will be setting up all unique associations here.
  setEnrollmentType(type: any) {
    this.selected_enrollment = type;
  }

  removeEnrollmentType() {
    this.selected_enrollment = null;
  }

  getEnrollmentType() {
    const enrollmentTypes = this.selected_enrollment;
    return enrollmentTypes ? JSON.parse(enrollmentTypes) : [];
  }

  private static readonly ENROLL_TYPE_PRIORITY = [
    'provider',
    'authRepresentative',
    'poa',
    'legalGuardian',
    'conservator',
    'vendor',
    'contractor',
    'employer',
    'independentFacilitator',
  ];

  private resolveEnrollType(): string {
    const enrollmentType = this.getEnrollmentType();
    if (enrollmentType && Array.isArray(enrollmentType) && enrollmentType.length > 1) {
      return SectionsService.ENROLL_TYPE_PRIORITY.find(t => enrollmentType.includes(t)) ?? enrollmentType[0];
    }
    return enrollmentType[0];
  }

  // we will be returning the single provider type here to load all configurations and prioritize if multiple provider types are there for a provider enrollment flow.
  getProviderFlowType(enrollmentTypes: any) {
    if (enrollmentTypes && Array.isArray(enrollmentTypes) && enrollmentTypes.length > 0) {
      const match = SectionsService.ENROLL_TYPE_PRIORITY.find(t => enrollmentTypes.includes(t));
      return match ? [match] : enrollmentTypes[0];
    }
    return null;
  }

  async getJsonConfigurationData(
    urlTeamplate: string,
    entry?: any,
    comp?: any
  ) {
    const program = this.userService.getSelectedProgram();
    const code = program?.program_code ?? '';
    const flow = entry === 'auth' ? 'authorizations' : entry;

    // When multiple provider types exist, fetch each config and merge
    if (comp && Array.isArray(comp) && comp.length > 1) {
      const uniqueTypes = [...new Set<string>(comp)];
      if (uniqueTypes.length > 1) {
        return this.fetchAndMergeJsonConfigs(urlTeamplate, code, flow, uniqueTypes);
      }
      comp = uniqueTypes[0];
    }

    const url = formatUrl(urlTeamplate, { code, flow, comp });

    if (url) {
      const resp = await fetch(url)
        .then((response) => response.json())
        .catch(() =>
          this.notificationService.alert(
            'error',
            'Error Fetching Data',
            'Error fetching configuration data'
          )
        );

      return await resp;
    } else {
      return false;
    }
  }

  /**
   * Fetches JSON configs for each provider type and merges section-level attributes/validations.
   * Falls back to the primary type if any fetch fails.
   */
  private async fetchAndMergeJsonConfigs(urlTemplate: string, code: string, flow: string, types: string[]) {
    const fetchResults = await Promise.all(
      types.map(comp =>
        fetch(formatUrl(urlTemplate, { code, flow, comp }))
          .then(response => response.json())
          .catch(() => null)
      )
    );

    const configs = fetchResults.filter(c => c != null);
    if (configs.length === 0) {
      this.notificationService.alert('error', 'Error Fetching Data', 'Error fetching configuration data');
      return false;
    }

    if (configs.length === 1) {
      return configs[0];
    }

    // Merge each section key across the UNION of all configs' sections. Seeding only from
    // configs[0] would drop any section that exists solely in a later config (e.g. an
    // AR + IF enrollment loses payment_method / prof_licences / service_rendered, which only
    // the IF config defines). Collect every section key, then merge or take the sole definer.
    const merged: any = {};
    const allSectionKeys = new Set<string>();
    configs.forEach(c => Object.keys(c ?? {}).forEach(k => allSectionKeys.add(k)));

    for (const sectionKey of allSectionKeys) {
      const definedSections = configs
        .map(c => c?.[sectionKey])
        .filter(s => s != null);

      const mergeableSections = definedSections.filter(s => s?.attributes);

      if (mergeableSections.length > 1) {
        // Section present (with attributes) in multiple configs → merge their attributes.
        merged[sectionKey] = this.mergeSectionConfigs(mergeableSections);
      } else {
        // Section present in a single config (or has no attributes) → keep the first definer,
        // preserving config order.
        merged[sectionKey] = definedSections[0];
      }
    }

    return merged;
  }

  getAuditHistoryColumns(flow: any, code: any) {
    return require(`../../assets/data/programs/${code}/audit/${flow}.json`);
  }

  getAssociationFlows(){
    // need to fetch flow from constants or config file based on the program, currently hardcoding for CASDP as of now.
    return ['employer', 'authRepresentative', 'provider', 'vendor', 'contractor', 'legalGuardian', 'poa', 'conservator', 'independentFacilitator'];
  }
}
